import {
  ValidationError,
  ValidationRuleId,
} from '@/lib/graphql/generated/graphql';
import en from '@/lib/lang/en.json';
import { ExtendedValidationError } from '@/types/validation-error';
import { TFunction } from 'i18next';

// Ensure all ValidationRuleId values exist in en.json translation file
type ValidationErrors = keyof typeof en.validation_errors;
type MissingTranslationKeys = Exclude<ValidationRuleId, ValidationErrors>;
// This constant will cause a type error if any ruleId is missing from en.json
const _assertAllRulesTranslated: MissingTranslationKeys extends never
  ? true
  : false = true;

/**
 * Maps French field names from backend to their translation keys
 * Note: Some field names may differ slightly from the translation file (e.g., "Prix de vente" vs "Prix Public TTC")
 * but they refer to the same concept and map to the same translation key.
 */
const FRENCH_FIELD_NAME_TO_TRANSLATION_KEY: Record<string, string> = {
  'Catégorie de produit': 'kpi.type_long',
  'Nom du produit': 'kpi.name',
  'Prix de vente': 'kpi.list_price', // Backend uses "Prix de vente", translation file has "Prix Public TTC" but both map to same concept
  'Discount Choose': 'kpi.choose_discount',
  'Prix de rétrocession': 'kpi.buying_price',
  'Prix Choose': 'kpi.choose_price',
  Marge: 'kpi.margin_percentage',
  'Frais de livraison': 'kpi.shipping_price',
  'Pays de fabrication': 'kpi.made_in',
  "Date d'expiration du coupon": 'kpi.coupon_expiration_date',
  "Conditions d'utilisation du coupon": 'kpi.coupon_terms',
  'Comment utiliser le coupon': 'kpi.coupon_how_to_use',
};

/**
 * Translates a list of French field names to the current language
 */
/**
 * Handles variants: fr, fr-FR, en, en-GB, en-US, etc.
 */
const normalizeI18nLanguage = (language: string): 'fr' | 'en' => {
  const normalized = language.toLowerCase().split('-')[0];
  return normalized === 'en' ? 'en' : 'fr';
};

export function translateFieldNames(
  fieldNames: string,
  t: TFunction,
  currentLanguage: string
): string {
  // Gère toutes les variantes : fr, fr-FR, en, en-GB, en-US, etc.
  if (normalizeI18nLanguage(currentLanguage) === 'fr') {
    return fieldNames;
  }

  // Split the field names by comma
  const fields = fieldNames.split(',').map((field) => field.trim());

  // Translate each field name
  const translatedFields = fields.map((field) => {
    const translationKey = FRENCH_FIELD_NAME_TO_TRANSLATION_KEY[field];
    if (translationKey) {
      const translated = t(translationKey);
      // If translation returns the key itself, fallback to original
      return translated !== translationKey ? translated : field;
    }
    // If no mapping found, return original (for fields like "Discount Choose" that are already in English)
    return field;
  });

  return translatedFields.join(', ');
}

/**
 * Extracts variables from the original French validation error message
 * to use in the translated message
 */
function extractVariablesFromMessage(
  ruleId: ValidationRuleId,
  originalMessage: string,
  t: TFunction,
  currentLanguage: string
): Record<string, string> {
  const variables: Record<string, string> = {};

  switch (ruleId) {
    case ValidationRuleId.DuplicateVariant: {
      // Extract name and options from: Des items avec le même nom ... partagent les mêmes options : ...
      const nameMatch = originalMessage.match(
        /le même nom\s+(.+?)\s+partagent/
      );
      if (nameMatch) {
        variables.name = nameMatch[1].trim();
      }
      const optionsMatch = originalMessage.match(/options\s*:\s*(.+)$/);
      if (optionsMatch) {
        variables.options = optionsMatch[1].trim();
      }
      break;
    }
    case ValidationRuleId.DuplicateSku: {
      // Extract SKU from: SKU "..." en doublon...
      const skuMatch = originalMessage.match(/SKU\s+"([^"]+)"/);
      if (skuMatch) {
        variables.sku = skuMatch[1];
      }
      break;
    }
    case ValidationRuleId.DuplicateGtin: {
      // Extract GTIN from: SKU "..." en doublon... (backend message says SKU but it's actually GTIN)
      // Or: GTIN "..." en doublon...
      const gtinMatch = originalMessage.match(/(?:SKU|GTIN)\s+"([^"]+)"/);
      if (gtinMatch) {
        variables.gtin = gtinMatch[1];
      }
      break;
    }
    case ValidationRuleId.RequiredFields: {
      // Extract fields from: Champs obligatoires manquants: ...
      const fieldsMatch = originalMessage.match(/manquants:\s*(.+)$/);
      if (fieldsMatch) {
        const frenchFields = fieldsMatch[1].trim();
        // Translate the field names
        variables.fields = translateFieldNames(
          frenchFields,
          t,
          currentLanguage
        );
      }
      break;
    }
    case ValidationRuleId.PriceCompetitiveness: {
      // Extract prices from: Le prix remisé TTC (X €) est inférieur au prix Choose (Y €)
      const discountedPriceMatch = originalMessage.match(
        /TTC\s*\(([^)]+)\s*€\)/
      );
      if (discountedPriceMatch) {
        variables.discountedPrice = discountedPriceMatch[1].trim();
      }
      const choosePriceMatch = originalMessage.match(
        /Choose\s*\(([^)]+)\s*€\)/
      );
      if (choosePriceMatch) {
        variables.choosePrice = choosePriceMatch[1].trim();
      }
      break;
    }
  }

  return variables;
}

/**
 * Rules whose backend message is per-product prose (an LLM verdict) rather than a template.
 * Their message is displayed verbatim instead of being resolved from the translation files.
 */
const RULE_IDS_WITH_BACKEND_MESSAGE: ReadonlySet<ValidationRuleId> = new Set([
  ValidationRuleId.VatCoherence,
]);

/**
 * Label the backend prefixes every `vat_coherence` message with
 * (`VatCoherenceRule.name`, see `buildVatErrorMessage`).
 */
const VAT_COHERENCE_MESSAGE_LABEL = 'TVA incorrecte';

/**
 * Splits a `vat_coherence` backend message into its two displayable parts.
 *
 * The judge writes `"TVA incorrecte: <reason> (source : <url>)"`, the source
 * being optional. The dialog shows the reason as prose and the source as a
 * link, so both have to be separated — and nothing may be lost:
 *
 * - the label is stripped only when it is exactly the one the backend writes,
 *   so a message in any other shape comes back whole;
 * - the source is detached only when it is a real http(s) URL. Anything else
 *   (a `javascript:` payload, a stray word) stays inside the reason as text: the
 *   verdict is LLM-written from brand-supplied descriptions, and must never
 *   reach an `href`.
 */
export const parseVatCoherenceMessage = (
  message: string
): { reason: string; sourceUrl: string | null } => {
  const sourceMatch = message.match(/\s*\(source\s*:\s*(\S+)\)\s*$/);
  const sourceUrl = sourceMatch ? toSafeHttpUrl(sourceMatch[1]) : null;

  const withoutSource =
    sourceMatch && sourceUrl
      ? message.slice(0, sourceMatch.index).trimEnd()
      : message.trim();

  const labelPrefix = `${VAT_COHERENCE_MESSAGE_LABEL}:`;
  const reason = withoutSource.startsWith(labelPrefix)
    ? withoutSource.slice(labelPrefix.length).trim()
    : withoutSource;

  return { reason: reason || withoutSource, sourceUrl };
};

/** Returns the URL only when it is safe to put in an `href`, else `null`. */
const toSafeHttpUrl = (value: string): string | null => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? value : null;
  } catch {
    return null;
  }
};

/**
 * Determines if the positive-stock error is for zero stock
 */
function isZeroStockError(originalMessage: string): boolean {
  return originalMessage.includes('Le stock est à zéro');
}

/**
 * Translates a ValidationError based on its ruleId and current language
 * @param error - The validation error from the backend (in French)
 * @param t - The translation function from react-i18next
 * @param currentLanguage - The current language code (e.g., 'en', 'fr')
 * @returns A new ValidationError object with translated fields
 */
export function translateValidationError(
  error: ValidationError,
  t: TFunction,
  currentLanguage: string = 'fr'
): ExtendedValidationError {
  const ruleId = error.ruleId;
  // The type check above (_assertAllRulesTranslated) ensures ruleId is a valid key in en.json
  const translationKey = `validation_errors.${ruleId}`;

  const translatedRuleName = t(`${translationKey}.ruleName`, {
    defaultValue: null,
  });

  // If no translation exists or language is French, return original
  if (!translatedRuleName) {
    return {
      ...error,
      ruleName: ruleId,
      fixSuggestion: ruleId,
    };
  }

  // Rules whose message is written by the backend for this specific product (LLM verdict).
  // There is nothing to look up in the translation files: the explanation IS the message, so we
  // pass it through untouched and only translate the surrounding label and suggestion.
  if (RULE_IDS_WITH_BACKEND_MESSAGE.has(ruleId)) {
    return {
      ...error,
      ruleName: translatedRuleName,
      message: error.message,
      fixSuggestion: t(`${translationKey}.fixSuggestion`, {
        defaultValue: ruleId,
      }),
    };
  }

  // Special handling for positive-stock rule (has two different messages)
  if (
    ruleId === ValidationRuleId.PositiveStock &&
    isZeroStockError(error.message)
  ) {
    return {
      ...error,
      ruleName: translatedRuleName,
      message: t(`${translationKey}.message_zero`),
      fixSuggestion: t(`${translationKey}.fixSuggestion_zero`, {
        defaultValue: ruleId,
      }),
    };
  }

  // Extract variables from original message for dynamic translations
  const variables = extractVariablesFromMessage(
    ruleId,
    error.message,
    t,
    currentLanguage
  );

  // Translate message with variables
  const translatedMessage = t(`${translationKey}.message`, {
    ...variables,
    defaultValue: error.message,
  });

  // Translate fixSuggestion if it exists
  const translatedFixSuggestion = t(`${translationKey}.fixSuggestion`, {
    ...variables,
    defaultValue: ruleId,
  });

  return {
    ...error,
    ruleName: translatedRuleName,
    message: translatedMessage,
    fixSuggestion: translatedFixSuggestion,
  };
}
